import type { ToolEvent } from '../hooks/useChatStream'

export type ToolOutcomeStatus = 'pending' | 'completed' | 'success' | 'failed'

interface ToolCopy {
  pending: string
  success: string
  failed: string
  description: string
}

const TOOL_COPY: Record<string, ToolCopy> = {
  authenticate_saviynt: {
    pending: 'Authenticating to Saviynt', success: 'Authenticated to Saviynt', failed: 'Saviynt authentication failed',
    description: 'Established a server-side session with the configured Saviynt tenant.',
  },
  get_connection_details: {
    pending: 'Retrieving connection details', success: 'Retrieved connection details', failed: 'Could not retrieve connection details',
    description: 'Read the existing connection state for this workspace.',
  },
  list_connections: {
    pending: 'Loading Saviynt connections', success: 'Loaded Saviynt connections', failed: 'Could not load Saviynt connections',
    description: 'Queried the connections available in the configured tenant.',
  },
  test_connection: {
    pending: 'Testing the connection', success: 'Connection test passed', failed: 'Connection test failed',
    description: 'Ran the configured connectivity and authentication checks.',
  },
  save_connection: {
    pending: 'Saving the connection', success: 'Saved the connection', failed: 'Could not save the connection',
    description: 'Persisted the reviewed connection configuration.',
  },
  create_connection: {
    pending: 'Creating the connection', success: 'Created the connection', failed: 'Could not create the connection',
    description: 'Submitted a new connector configuration to Saviynt.',
  },
  deploy_via_terraform: {
    pending: 'Deploying through Terraform', success: 'Pushed the Terraform deployment', failed: 'Terraform deployment failed',
    description: 'Published the sanitized configuration to the configured repository workflow.',
  },
  rollback_connection: {
    pending: 'Preparing the rollback', success: 'Rolled back the connection', failed: 'Rollback failed',
    description: 'Applied the requested reviewed rollback operation.',
  },
  web_search: {
    pending: 'Searching documentation', success: 'Searched documentation', failed: 'Documentation search failed',
    description: 'Looked for supporting public documentation.',
  },
  ingest_document: {
    pending: 'Processing the document', success: 'Processed the document', failed: 'Document processing failed',
    description: 'Added the supplied document to project-scoped retrieval.',
  },
  upload_schema_file: {
    pending: 'Uploading the schema file', success: 'Uploaded the schema file', failed: 'Schema upload failed',
    description: 'Uploaded the reviewed schema artifact.',
  },
}

const SENSITIVE_KEY = /(password|passwd|token|secret|api.?key|authorization|cookie|credential|client.?id)/i
const SAFE_ACCESS_TOKEN_REFERENCE = /\$\{\s*access(?:\\?[_-])?token\s*\}/i

function isSafeCredentialReference(value: unknown): value is string {
  return typeof value === 'string' && (
    value.includes('{{secret:')
    || value.includes('{{base64:')
    || SAFE_ACCESS_TOKEN_REFERENCE.test(value)
  )
}

export function sanitizeToolPayload(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sanitizeToolPayload)
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [
    key,
    SENSITIVE_KEY.test(key) && !isSafeCredentialReference(item) ? '[redacted]' : sanitizeToolPayload(item),
  ]))
}

export function toolOutcomeStatus(tool: ToolEvent, historical = false): ToolOutcomeStatus {
  if (!tool.result) return historical ? 'completed' : 'pending'
  if (tool.result.success === false || tool.result.error) return 'failed'
  return 'success'
}

export function toolOutcomeCopy(tool: ToolEvent, historical = false) {
  const status = toolOutcomeStatus(tool, historical)
  const fallbackName = tool.name.replace(/_/g, ' ')
  const copy = TOOL_COPY[tool.name] || {
    pending: `Running ${fallbackName}`,
    success: `Completed ${fallbackName}`,
    failed: `${fallbackName} failed`,
    description: 'Completed an agent tool action for this conversation.',
  }
  const resultMessage = tool.result && (
    typeof tool.result.message === 'string' ? tool.result.message
      : typeof tool.result.error === 'string' ? tool.result.error
        : null
  )
  if (status === 'completed') {
    return {
      title: `Recorded ${fallbackName}`,
      description: 'Historical tool activity; this legacy record did not retain its outcome.',
      resultMessage: null,
    }
  }
  return { title: copy[status], description: copy.description, resultMessage }
}
